import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number // rate from USD
  locale: string
}

const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  CO: { code: 'COP', symbol: '$', rate: 4150, locale: 'es-CO' },
  MX: { code: 'MXN', symbol: '$', rate: 17.2, locale: 'es-MX' },
  AR: { code: 'ARS', symbol: '$', rate: 875, locale: 'es-AR' },
  PE: { code: 'PEN', symbol: 'S/', rate: 3.75, locale: 'es-PE' },
  CL: { code: 'CLP', symbol: '$', rate: 935, locale: 'es-CL' },
  BR: { code: 'BRL', symbol: 'R$', rate: 5.0, locale: 'pt-BR' },
  EC: { code: 'USD', symbol: '$', rate: 1, locale: 'es-EC' },
  VE: { code: 'USD', symbol: '$', rate: 1, locale: 'es-VE' },
  UY: { code: 'UYU', symbol: '$', rate: 39.5, locale: 'es-UY' },
  BO: { code: 'BOB', symbol: 'Bs', rate: 6.91, locale: 'es-BO' },
  PY: { code: 'PYG', symbol: '₲', rate: 7300, locale: 'es-PY' },
  CR: { code: 'CRC', symbol: '₡', rate: 530, locale: 'es-CR' },
  DO: { code: 'DOP', symbol: 'RD$', rate: 57, locale: 'es-DO' },
  GT: { code: 'GTQ', symbol: 'Q', rate: 7.85, locale: 'es-GT' },
  HN: { code: 'HNL', symbol: 'L', rate: 24.7, locale: 'es-HN' },
  NI: { code: 'NIO', symbol: 'C$', rate: 36.7, locale: 'es-NI' },
  PA: { code: 'USD', symbol: '$', rate: 1, locale: 'es-PA' },
  SV: { code: 'USD', symbol: '$', rate: 1, locale: 'es-SV' },
  US: { code: 'USD', symbol: '$', rate: 1, locale: 'en-US' },
}

const DEFAULT_CURRENCY: CurrencyInfo = {
  code: 'USD',
  symbol: '$',
  rate: 1,
  locale: 'en-US',
}

export function useCurrency() {
  const [currency, setCurrency] = useState<CurrencyInfo>(() => {
    try {
      const saved = localStorage.getItem('user-currency')
      if (saved) return JSON.parse(saved)
    } catch {}
    return DEFAULT_CURRENCY
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const saved = localStorage.getItem('user-currency')
    if (saved) {
      try {
        setCurrency(JSON.parse(saved))
        setLoading(false)
        return
      } catch {}
    }

    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        const countryCode = data?.country_code || 'US'
        const detected = CURRENCY_MAP[countryCode] || DEFAULT_CURRENCY
        setCurrency(detected)
        localStorage.setItem('user-currency', JSON.stringify(detected))
      })
      .catch(() => {
        setCurrency(DEFAULT_CURRENCY)
      })
      .finally(() => setLoading(false))
  }, [])

  const formatPrice = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate

    // For currencies with large values, don't show decimals
    if (currency.rate >= 100) {
      const rounded = Math.round(converted / 100) * 100
      return `${currency.symbol}${rounded.toLocaleString(currency.locale)}`
    }

    // For currencies with small rates, show 2 decimals
    return `${currency.symbol}${converted.toLocaleString(currency.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const formatPriceParts = (
    usdAmount: number,
  ): {
    symbol: string
    whole: string
    decimal: string
    currencyCode: string
  } => {
    const converted = usdAmount * currency.rate

    if (currency.rate >= 100) {
      const rounded = Math.round(converted / 100) * 100
      return {
        symbol: currency.symbol,
        whole: rounded.toLocaleString(currency.locale),
        decimal: '',
        currencyCode: currency.code,
      }
    }

    const whole = Math.floor(converted)
    const decimal = Math.round((converted - whole) * 100)
      .toString()
      .padStart(2, '0')

    return {
      symbol: currency.symbol,
      whole: whole.toLocaleString(currency.locale),
      decimal: `.${decimal}`,
      currencyCode: currency.code,
    }
  }

  return {
    currency,
    loading,
    formatPrice,
    formatPriceParts,
  }
}
